import {
  Menu,
  Popover,
  Select,
  Spinner,
  FilterButton,
  ActionsButton,
} from '@skupreme/shared/ui';
import { useNavigate } from 'react-router';
import { CatalogSearchInput, flagImages } from '../../legacy-bridge';

type Region = { key: string; text: string; name?: string };

export type CatalogItemsActionsProps = {
  regions: Region[];
  regionId: string | null;
  onRegionChange: (regionId: string) => void;
  regionLoading?: boolean;
  /** The marketplace stats the sync alert reads are still loading. */
  statsLoading?: boolean;
  onSearch: (search: string) => void;
  onAddItem: () => void;
  onBulkUpload: () => void;
  /** Adds "Export inventory" to the Actions menu when given. */
  onExport?: () => void;
  readOnly?: boolean;
};

function flagFor(region: Region) {
  return flagImages[(region.name || '').toUpperCase()] ?? flagImages.WW;
}

/** Search, the region filter and the actions: the items page's controls on the tabs' row. */
export function CatalogItemsActions({
  regions,
  regionId,
  onRegionChange,
  regionLoading,
  statsLoading,
  onSearch,
  onAddItem,
  onBulkUpload,
  onExport,
  readOnly,
}: CatalogItemsActionsProps) {
  const navigate = useNavigate();
  const activeRegion = regions.find(region => region.key === regionId);

  const actions = [
    { id: 'new', label: 'Add new item', isDisabled: readOnly, run: onAddItem },
    {
      id: 'bulk',
      label: 'Upload items in bulk',
      isDisabled: readOnly,
      run: onBulkUpload,
    },
    ...(onExport
      ? [{ id: 'export', label: 'Export inventory', run: onExport }]
      : []),
  ];

  return (
    <>
      {statsLoading ? <Spinner size="sm" label="Loading stats" /> : null}
      <CatalogSearchInput
        regionId={regionId}
        setTableSearch={onSearch}
        onResultClick={catalog =>
          navigate(`/app/catalogs/catalog/${encodeURIComponent(catalog._id)}`)
        }
      />
      <Popover
        aria-label="Filters"
        placement="bottom end"
        trigger={
          <FilterButton>
            {activeRegion ? (
              <img
                src={flagFor(activeRegion)}
                alt={activeRegion.text}
                title={activeRegion.text}
                className="sk:h-3 sk:w-4 sk:rounded-xs sk:object-cover"
              />
            ) : null}
          </FilterButton>
        }
      >
        <Select
          aria-label="Marketplace region"
          placeholder="Marketplace region"
          isDisabled={regionLoading}
          value={regionId ?? undefined}
          onChange={onRegionChange}
          options={regions.map(region => ({
            value: region.key,
            label: region.text,
            leading: (
              <img
                src={flagFor(region)}
                alt=""
                className="sk:h-3 sk:w-4 sk:rounded-xs sk:object-cover"
              />
            ),
          }))}
          className="sk:w-56"
        />
      </Popover>
      <Menu
        aria-label="Actions"
        trigger={<ActionsButton />}
        actions={actions}
        onAction={id => actions.find(action => action.id === id)?.run()}
      />
    </>
  );
}
